import { Box, Chip, Dialog, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../atoms/Typography/Typography';
import { Icon } from '../../atoms/Icon/Icon';
import { DialogCloseButton } from '../../atoms/DialogCloseButton/DialogCloseButton';
import { getFlow2PriorityPillSx } from '../GoalPriorityChip/GoalPriorityChip';
import { GoalTypeIconSmall, FTDS_MODAL_SHADOW } from '../../../themes/styles/common';
import { getGoalTypeBadge } from '../../../utils/goalTypeBadgeMapper';
import { ContributionScheduleTable, ContributionScheduleAccount } from './ContributionScheduleTable';

type Props = {
    open: boolean;
    onClose: () => void;
    goalName?: string;
    iconType?: string;
    iconColor?: string;
    iconBgColor?: string;
    priorityLabel?: string;
    targetDate?: string;
    accounts: ContributionScheduleAccount[];
    subtitle?: string;
    // Optional description shown below the goal meta (Funding Navigator schedule only).
    scheduleDescription?: string;
    hideGoalMeta?: boolean;
    firstColumnLabel?: string;
    firstColumnWidth?: string;
    showNowColumn?: boolean;
    showTotalRow?: boolean;
    totalRowValues?: { date: string; value: number }[];
    trailingEmptyStateLabel?: string;
    horizontalScroll?: boolean;
    stickyFirstColumn?: boolean;
    maxHeight?: string;
    bodyRowHeight?: string;
    contributionsHeaderInfo?: string;
    // eslint-disable-next-line no-unused-vars
    renderNameCell?: (row: any) => ReactNode;
};

export const ContributionScheduleModal = ({
    open,
    onClose,
    goalName,
    iconType,
    iconColor,
    iconBgColor,
    priorityLabel,
    targetDate,
    accounts,
    subtitle,
    scheduleDescription,
    hideGoalMeta = false,
    firstColumnLabel,
    firstColumnWidth,
    showNowColumn = false,
    showTotalRow = false,
    totalRowValues,
    trailingEmptyStateLabel,
    horizontalScroll = false,
    stickyFirstColumn = false,
    maxHeight,
    bodyRowHeight,
    contributionsHeaderInfo,
    renderNameCell
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const GoalBadge = getGoalTypeBadge(iconType);

    const scrollbarSx = {
        '& .MuiTableContainer-root': {
            '&::-webkit-scrollbar': { width: '8px', height: '8px', backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-track-piece': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.neutral[400],
                borderRadius: '100px'
            },
            '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
            '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '32px',
                    boxShadow: FTDS_MODAL_SHADOW,
                    maxWidth: '95vw',
                    width: '1038px',
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogCloseButton handleClose={onClose} />
            <Typography
                variant="headers.h3"
                color="semantic.text.brand"
                label={t('CONTRIBUTION_SCHEDULE_TITLE')}
                sx={{ fontSize: '24px', lineHeight: '30px', fontWeight: 700 }}
            />
            {hideGoalMeta ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <Typography
                        variant="labels.large"
                        color="neutral.black"
                        label={subtitle ?? ''}
                        sx={{ fontWeight: 400, fontSize: '13px' }}
                    />
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {GoalBadge ? (
                                <GoalBadge width={22} height={22} style={{ flexShrink: 0 }} />
                            ) : (
                                <GoalTypeIconSmall
                                    color={iconColor}
                                    backgroundColor={iconBgColor}
                                    sx={{
                                        width: '22px',
                                        height: '22px',
                                        padding: '4px',
                                        '& svg': { width: '12px', height: '12px' }
                                    }}
                                >
                                    <Icon id={iconType ?? ''} size={12} />
                                </GoalTypeIconSmall>
                            )}
                            <Typography
                                variant="labels.large"
                                color="semantic.text.default"
                                label={goalName ?? ''}
                                sx={{ fontWeight: 400, fontSize: '16px', lineHeight: '22px' }}
                            />
                        </Box>
                        {priorityLabel && (
                            <Chip
                                label={priorityLabel}
                                size="small"
                                variant="outlined"
                                sx={getFlow2PriorityPillSx(theme, true)}
                            />
                        )}
                    </Box>
                </>
            )}
            {scheduleDescription && (
                <Box sx={{ marginBottom: '12px' }}>
                    <Typography
                        variant="labels.large"
                        color="semantic.text.default"
                        label={scheduleDescription}
                        sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '19px', marginTop: '24px' }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    marginTop: '24px',
                    border: `1px solid ${theme.palette.semantic.bd.subtle}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minHeight: 0,
                    flexGrow: 0,
                    flexShrink: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    '& > div': { flexGrow: 0, flexShrink: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
                    ...scrollbarSx,
                    '& .MuiTableContainer-root': {
                        ...scrollbarSx['& .MuiTableContainer-root'],
                        flexGrow: 0,
                        flexShrink: 1,
                        minHeight: 0
                    }
                }}
            >
                <ContributionScheduleTable
                    accounts={accounts}
                    targetDate={targetDate}
                    firstColumnLabel={firstColumnLabel}
                    firstColumnWidth={firstColumnWidth}
                    showNowColumn={showNowColumn}
                    showTotalRow={showTotalRow}
                    totalRowValues={totalRowValues}
                    trailingEmptyStateLabel={trailingEmptyStateLabel}
                    horizontalScroll={horizontalScroll}
                    stickyFirstColumn={stickyFirstColumn}
                    maxHeight={maxHeight}
                    bodyRowHeight={bodyRowHeight}
                    contributionsHeaderInfo={contributionsHeaderInfo}
                    renderNameCell={renderNameCell}
                />
            </Box>
        </Dialog>
    );
};
